import { z as zod, ZodTypeAny } from 'zod';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { schemaHelper } from './schema-helper';
import FormConstants from 'src/constants/FormConstants';

const validationMapping: Record<string, (opts?: any) => ZodTypeAny> = {
  name: schemaHelper.name,
  email: schemaHelper.email,
  phoneNumber: schemaHelper.phoneNumber,
  text: schemaHelper.text,
  date: schemaHelper.date,
  number: schemaHelper.number,
  floatNumber: schemaHelper.floatNumber,
  boolean: schemaHelper.boolean,
  array: schemaHelper.array,
};

export interface ModalRowColumn {
  name: string;
  label?: string;
  heading?: string;
  type: string;
  required?: boolean;
  validation_schema?: string;
  options?: { label: string; value: any }[];
  columns?: ModalRowColumn[];
}

/**
 * Build a Zod schema for a single row of the table.
 * Used only for validating the Add/Edit Entry modal – separate from the main form schema.
 */
export function buildModalRowSchema(
  columns: ModalRowColumn[],
  t: (key: string) => string
): zod.ZodObject<Record<string, ZodTypeAny>> {
  const shape = columns.reduce<Record<string, ZodTypeAny>>((acc, col) => {
    if (col.type === FormConstants.Field_Types.TABLE_INPUT && col.columns?.length) {
      const nested = buildModalRowSchema(col.columns, t);
      acc[col.name] = col.required
        ? zod.array(nested).min(1, `${t(col.label ?? 'Field')} must contain at least one row!`)
        : zod.array(nested).optional();
      return acc;
    }

    const vfn = col.validation_schema
      ? validationMapping[col.validation_schema]
      : undefined;
    const labelOrHeading = (col.label ?? col.heading ?? 'Field').trim() || 'Field';
    const base =
      typeof vfn === 'function'
        ? vfn({
            message: {
              required_error: `${t(labelOrHeading)} is required!`,
              invalid_type_error: `${t(labelOrHeading)} is invalid!`,
            },
            required: !!col.required,
            isValidPhoneNumber,
          })
        : (vfn ?? zod.string());

    acc[col.name] = col.required
      ? base
      : zod.preprocess(
          (v) => (v === '' || v === undefined ? undefined : v),
          base.optional()
        );
    return acc;
  }, {});

  return zod.object(shape);
}

/** Flatten Zod errors for a row into { fieldName: message } for modal display */
export function getModalRowErrors(
  error: zod.ZodError
): Record<string, string> {
  const out: Record<string, string> = {};
  error.errors.forEach((e) => {
    const path = e.path[0];
    if (path != null && typeof path === 'string') {
      out[path] = e.message;
    }
  });
  return out;
}
